import { setAvailableModels, setEnabledFeatures } from '../slices/globalSlice';
import { SESSION_KEYS } from 'utils/constant';

/**
 * @file initialDataMiddleware.js
 * @description Redux middleware for initializing app data upon user authentication.
 *
 * Initialization sequence on auth success:
 * 1. Fetch feature flags and AI models in parallel
 * 2. Dispatch data to Redux store
 * 3. Conditionally establish WebSocket connection based on feature flags
 */

// Track initialization state
let hasLoadedInitialData = false;

// Auth action types
const AUTH_SUCCESS_ACTIONS = ['auth/loginSuccess', 'auth/refreshTokenSuccess'];
const LOGOUT_ACTIONS = ['auth/logout', 'auth/logoutSuccess'];

/**
 * Reset initialization flag (for logout/testing)
 */
export const resetInitialDataFlag = () => {
  hasLoadedInitialData = false;
};

/**
 * Get auth token from available sources
 */
const getAuthToken = (store, action) => {
  return (
    action.payload?.token ||
    store.getState().auth.token ||
    sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN) ||
    localStorage.getItem(SESSION_KEYS.AUTH_TOKEN)
  );
};

/**
 * Connect WebSocket if feature is enabled
 */
const connectWebSocketIfEnabled = async (store, action, featureFlags) => {
  if (featureFlags?.websocket_chat === false) {
    console.log(
      '🔌 [InitialDataMiddleware] WebSocket chat feature is disabled'
    );
    return;
  }

  const token = getAuthToken(store, action);
  if (!token) {
    console.warn('⚠️ [InitialDataMiddleware] No token found for WebSocket');
    return;
  }

  try {
    const { default: websocketService } = await import(
      '../../api/services/websocketService'
    );

    const apiUrl = new URL(
      process.env.REACT_APP_API_BASE_URL || 'https://localhost:8080'
    );
    const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${apiUrl.host}/v1/chat/ws`;

    websocketService.connect(wsUrl, token, store.dispatch, {}, store.getState);
  } catch (error) {
    console.error(
      '❌ [InitialDataMiddleware] WebSocket connection failed:',
      error
    );
  }
};

/**
 * Disconnect WebSocket
 */
const disconnectWebSocket = async () => {
  try {
    const { default: websocketService } = await import(
      '../../api/services/websocketService'
    );
    websocketService.disconnect();
  } catch (error) {
    console.error(
      '❌ [InitialDataMiddleware] WebSocket disconnect failed:',
      error
    );
  }
};

/**
 * Initialize app data and services after authentication
 */
const initializeApp = async (store, action) => {
  // 1. Fetch feature flags and AI models in parallel
  const [configModule, aiModule] = await Promise.all([
    import('../../api/services/config'),
    import('../../api/services/ai'),
  ]);

  const configService = configModule.configService || configModule.default;
  const aiService = aiModule.aiService || aiModule.default;

  const [featuresResult, modelsResult] = await Promise.allSettled([
    configService.getFeatures(),
    aiService.getAvailableModels(),
  ]);

  // 2. Process and dispatch results
  let featureFlags = null;

  if (featuresResult.status === 'fulfilled' && featuresResult.value.success) {
    featureFlags = featuresResult.value.data;
    store.dispatch(setEnabledFeatures(featureFlags));
  } else {
    console.error('❌ [InitialDataMiddleware] Failed to load feature flags');
  }

  if (modelsResult.status === 'fulfilled' && modelsResult.value.success) {
    store.dispatch(setAvailableModels(modelsResult.value.data.models));
  } else {
    console.error('❌ [InitialDataMiddleware] Failed to load AI models');
  }

  // 3. Conditionally connect WebSocket based on feature flag
  await connectWebSocketIfEnabled(store, action, featureFlags);
};

/**
 * Initial Data Middleware
 */
export const initialDataMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  if (AUTH_SUCCESS_ACTIONS.includes(action.type)) {
    const { user, isAuthenticated } = store.getState().auth;

    if (isAuthenticated && user) {
      if (!hasLoadedInitialData) {
        hasLoadedInitialData = true;
        initializeApp(store, action).catch((error) => {
          console.error(
            '❌ [InitialDataMiddleware] Initialization failed:',
            error
          );
          hasLoadedInitialData = false; // Allow retry on failure
        });
      } else if (action.type === 'auth/refreshTokenSuccess') {
        // Reconnect WebSocket with new token on token refresh
        const featureFlags = store.getState().global?.enabledFeatures;
        connectWebSocketIfEnabled(store, action, featureFlags);
      }
    }
  }

  if (LOGOUT_ACTIONS.includes(action.type)) {
    resetInitialDataFlag();
    disconnectWebSocket();
  }

  return result;
};

export default initialDataMiddleware;
